import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Input,
  Heading,
  Text,
} from '@chakra-ui/react';
import './SetPasswordForm.scss';
import { useTranslation } from 'react-i18next';
import AuthService from '../../services/AuthService.ts';
import { useParams } from 'react-router-dom';

interface SetPasswordFormProps {
  onBackToLoginClick: () => void;
  isTokenValid: boolean;
}

const SetPasswordForm: React.FC<SetPasswordFormProps> = ({
  onBackToLoginClick,
  isTokenValid,
}) => {
  const { t } = useTranslation();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [strengthError, setStrengthError] = useState('');
  const [matchError, setMatchError] = useState('');
  const { token } = useParams<{ token: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Strong password check: at least 1 lowercase, 1 uppercase, 1 digit, and 1 special character
  const isStrongPassword = (value: string): boolean =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/.test(value);

  
  const handlePasswordChange = (value: string): void => {
    setPassword(value);

    const newStrengthError = !isStrongPassword(value)
      ? t('passwordRules')
      : '';

    const newMatchError =
      passwordConfirm.trim() !== '' && value !== passwordConfirm
        ? t('passwordNotIdentical')
        : '';

    setStrengthError(newStrengthError);
    setMatchError(newMatchError);

    const bothFilled = value.trim() !== '' && passwordConfirm.trim() !== '';
    const hasError = !!newStrengthError || !!newMatchError;
    setIsButtonDisabled(!(bothFilled && !hasError));
  };

  const handlePasswordConfirmChange = (value: string): void => {
    setPasswordConfirm(value);

    // Password strength check
    const newStrengthError = !isStrongPassword(password)
      ? t('passwordRules')
      : '';

    const newMatchError =
      value.trim() !== '' && password !== value ? t('passwordNotIdentical') : '';

    setStrengthError(newStrengthError);
    setMatchError(newMatchError);

    const bothFilled = password.trim() !== '' && value.trim() !== '';
    const hasError = !!newStrengthError || !!newMatchError;
    setIsButtonDisabled(!(bothFilled && !hasError));
  };

  const handleSetPasswordClick = async (): Promise<void> => {
    const finalStrengthError = !isStrongPassword(password)
      ? t('passwordRules')
      : '';
    const finalMatchError = password !== passwordConfirm ? t('passwordNotIdentical') : '';

    if (finalStrengthError || finalMatchError) {
      setStrengthError(finalStrengthError);
      setMatchError(finalMatchError);
      return;
    }

    try {
      setIsSubmitting(true);
      await AuthService.resetPassword(token!, password, passwordConfirm);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Error in handleSetPasswordClick:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isTokenValid) {
    return (
      <Box textAlign="center" className="login-form">
        <Heading as="h1" size="md" fontWeight={500} mb={4}>
          Invalid or expired token
        </Heading>
        <Button onClick={onBackToLoginClick}>{t('backToLogin')}</Button>
      </Box>
    );
  }

  return (
    <Box textAlign="center" className="login-form">
      <Heading
        as="h1"
        size={{ base: 'md', md: 'lg' }}
        fontWeight={500}
        className="auth-title"
        mb={2}
      >
        {showSuccessMessage ? t('registrationSuccessfullHeader') : t('registration')}
      </Heading>

      <Text
        fontSize={{ base: 'sm', md: 'md' }}
        color="gray.600"
        mb={12}
        maxWidth="400px"
        textAlign="left"
      >
        {showSuccessMessage ? t('registrationSuccessfullSubline') : t('loginWelcome')}
      </Text>

      {!showSuccessMessage && (
        <>
          <FormControl id="password" mb={"45px"} isInvalid={!!strengthError}>
            <Input
              type="password"
              placeholder={t('setPassword')}
              variant="outline-only-bottom"
              color="black"
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              errorBorderColor="#EF5C36"
              focusBorderColor={strengthError ? '#EF5C36' : undefined}
              borderColor={strengthError ? '#EF5C36' : undefined}
              sx={{
                '&:focus': {
                  borderColor: strengthError ? '#EF5C36' : undefined,
                },
              }}
            />
            {strengthError && (
              <Text color="red.500" fontSize="sm" mt={1} textAlign="left">
                {strengthError}
              </Text>
            )}
          </FormControl>

          <FormControl id="passwordConfirm" mb={4} isInvalid={!!matchError}>
            <Input
              type="password"
              placeholder={t('repeatPassword')}
              variant="outline-only-bottom"
              color="black"
              value={passwordConfirm}
              onChange={(e) => handlePasswordConfirmChange(e.target.value)}
              errorBorderColor="#EF5C36"
              focusBorderColor={matchError ? '#EF5C36' : undefined}
              borderColor={matchError ? '#EF5C36' : undefined}
              sx={{
                '&:focus': {
                  borderColor: matchError ? '#EF5C36' : undefined,
                },
              }}
            />
            {matchError && (
              <Text color="red.500" fontSize="sm" mt={1} textAlign="left">
                {matchError}
              </Text>
            )}
          </FormControl>
        </>
      )}

      {showSuccessMessage ? (
        <Button
          colorScheme="alea"
          variant="solid"
          borderRadius={20}
          mb={4}
          mt={{ base: '46px', md: '76px' }}
          width={{ base: '100%', md: '300px' }}
          height={{ base: '45px', md: '50px' }}
          fontSize={{ base: '14px', md: '18px' }}
          fontWeight={400}
          onClick={onBackToLoginClick}
        >
          {t('login_Button')}
        </Button>
      ) : (
        <Button
          colorScheme="alea"
          variant="solid"
          borderRadius={20}
          type="submit"
          mb={4}
          mt={{ base: '46px', md: '76px' }}
          width={{ base: '100%', md: '300px' }}
          height={{ base: '45px', md: '50px' }}
          fontSize={{ base: '14px', md: '18px' }}
          fontWeight={400}
          isDisabled={isButtonDisabled}
          isLoading={isSubmitting}
          onClick={handleSetPasswordClick}
        >
          {t('register')}
        </Button>
      )}
    </Box>
  );
};

export default SetPasswordForm;